// 세리사무소 포털의 껍데기 — 호스트 Layout 을 고치는 대신 한 벌 더 둔다.
//
// 기획서가 요구하는 껍데기가 호스트의 것과 다르다: 상단바는 세무법인의 화이트라벨이고
// (6.1), 좌측 메뉴 대신 우측 56px 레일이 상주하며(7), 고정하면 본문이 옆으로 밀린다.
// 호스트 Layout 에 분기를 넣으면 기본 포털 쪽 코드가 세리사무소 사정으로 흔들린다.
//
// 모듈 패널(회계 등)을 이 껍데기도 keep-alive 로 갖고 있다. 업무 패널의 'ERP에서 열기'와
// 회사 콘솔의 메뉴가 실제로 모듈로 보내는데, 그때 껍데기가 호스트 것으로 바뀌면
// 사이드바와 컨텍스트 칩이 사라져 '회사를 갈아타며 연속 작업'이 성립하지 않는다.
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { usePathname } from '../../lib/navigation';
import { ModulePane, PlaceholderPane, moduleCodeForPath } from '../ModuleHost';
import HomeZeirishi from '../../pages/Home/Home.zeirishi';
import { ZeirishiProvider, useZeirishi } from '../../pages/Home/Home.zeirishi.context';
import TopNavZeirishi from './TopNav.zeirishi';
import SidebarZeirishi from './Sidebar.zeirishi';
import styles from './Layout.zeirishi.module.css';

/* keep-alive 대상 모듈 — 호스트 Layout 의 MODULE_CODES 와 같은 목록이다.
   한쪽에만 모듈을 추가하면 그 포털에서만 빈 화면이 나오므로 함께 고쳐야 한다. */
const MODULE_CODES = [
  'yukimushi-base-webpack-fe', 'yukimushi-base-vite-fe', 'yukimushi-board-fe',
  'yukimushi-onechamber-fe', 'yukimushi-fi-fe', 'yukimushi-oneffice-fe',
  'yukimushi-videoconf-fe', 'yukimushi-basicinfo-fe', 'yukimushi-pjt-fe',
  'yukimushi-hr-fe', 'yukimushi-ops-fe', 'yukimushi-tax-fe',
];

const PLACEHOLDERS: { prefix: string; label: string }[] = [
  { prefix: '/mail', label: '메일' },
  { prefix: '/messenger', label: '메신저' },
  { prefix: '/erp', label: 'ERP 회계' },
  { prefix: '/settings', label: '설정' },
];

/** 첫 방문 시 마운트 → 이후 display:none (호스트 Layout 과 같은 방식) */
function KeepAlive({ active, children }: { active: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(active);
  useEffect(() => { if (active) setMounted(true); }, [active]);
  if (!mounted) {
    return null;
  }
  return (
    <div style={active
      ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }
      : { display: 'none' }}
    >
      {children}
    </div>
  );
}

function Shell() {
  const pathname = usePathname();
  /* 열려 있는 패널·고정 여부는 컨텍스트에 있다 — 홈의 검색바도 "AI 패널을 열어라"라고
     말할 수 있어야 해서다. 사이드바의 동작 세 가지 중 둘이 이것이고, 나머지 하나인
     '새 창'은 호스트에 창을 띄우는 경로가 없어 토스트로만 알린다. */
  const { toastMsg, panel, pinned, closePanel } = useZeirishi();

  const activeModule = moduleCodeForPath(pathname);
  const activePlaceholder = !activeModule && pathname !== '/'
    ? PLACEHOLDERS.filter(p => pathname.indexOf(p.prefix) === 0)[0]
    : undefined;

  /* Esc 로 패널을 닫는다 — 입력 중에는 건드리지 않는다 */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !panel) {
        return;
      }
      // target 이 document 일 수 있다 — tagName 을 바로 읽으면 예외가 나 핸들러가 통째로 죽는다
      const el = e.target as HTMLElement | null;
      const tag = el && el.tagName ? el.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        return;
      }
      closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panel, closePanel]);

  return (
    <div className={`${styles.shell} ${pinned ? styles.shellPinned : ''}`}>
      <TopNavZeirishi />

      <main className={styles.main}>
        <KeepAlive active={pathname === '/'}>
          <HomeZeirishi />
        </KeepAlive>

        {MODULE_CODES.map(code => (
          <ModulePane key={code} code={code} active={activeModule === code} />
        ))}

        {activePlaceholder && <PlaceholderPane label={activePlaceholder.label} active />}
      </main>

      <SidebarZeirishi />

      {/* 토스트는 껍데기가 그린다 — 홈·사이드바·상단바 어디서 띄우든 같은 자리에 뜬다 */}
      <div className={`${styles.toast} ${toastMsg ? styles.toastOn : ''}`}>
        <span className={styles.toastCk}>✓</span>
        <span>{toastMsg}</span>
      </div>
    </div>
  );
}

export default function LayoutZeirishi() {
  return (
    <ZeirishiProvider>
      <Shell />
    </ZeirishiProvider>
  );
}
